import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { Enquiry, EnquiryStatus } from '@shared/api/enquiry/enquiry.interface';
import { Image } from '@shared/api/media/media.interface';
import { User } from '@shared/api/user/user.interface';
import { Loading } from '@shared/components/Loading';
import Tabs from '@shared/components/Tabs';
import { fetcher, queryString } from '@shared/hooks/recoil-query';
import { customThemeAtom } from '@shared/state/atom/theme.atom';
import { mainBgColorGenerator } from '@shared/utils/mainColorGenerator';
import { DatePicker, Input, Table, TableColumnsType, Tag } from 'antd';
import locale from 'antd/es/date-picker/locale/ko_KR';
import dayjs from 'dayjs';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';

import { useAdminTitleHook } from '../hooks/useAdminTitleHook';

const { RangePicker } = DatePicker;

type EnquiryPopulated = Enquiry<User, Image>;

const STATUS_LABEL: Record<EnquiryStatus, { text: string; color: string }> = {
  [EnquiryStatus.Todo]: { text: '대기', color: 'red' },
  [EnquiryStatus.Doing]: { text: '진행중', color: 'orange' },
  [EnquiryStatus.Done]: { text: '완료', color: 'green' },
};

const tabItems: readonly string[] = ['미처리', '완료'];

const AdminEnquiryListPage: FC = () => {
  useAdminTitleHook('문의 관리');

  const customTheme = useRecoilValue(customThemeAtom);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([
    dayjs().subtract(1, 'month'),
    dayjs(),
  ]);
  const [phoneSearch, setPhoneSearch] = useState<string>('');

  const pendingUrl = useMemo(
    () =>
      `admin/enquiry/?${queryString({
        filter: { status: { $ne: 'Done' } },
        options: { sort: { createdAt: -1 } },
        populate: [{ path: 'user' }, { path: 'image' }],
      })}`,
    [],
  );

  const doneUrl = useMemo(() => {
    const filter: Record<string, any> = { status: 'Done' };

    if (dateRange[0] && dateRange[1]) {
      filter.updatedAt = {
        $gte: dateRange[0].startOf('day').toISOString(),
        $lte: dateRange[1].endOf('day').toISOString(),
      };
    }

    if (phoneSearch.trim()) {
      filter.phoneNumber = { $regex: phoneSearch.trim() };
    }

    return `admin/enquiry/?${queryString({
      filter,
      options: { sort: { updatedAt: -1 } },
      populate: [{ path: 'user' }, { path: 'image' }],
    })}`;
  }, [dateRange, phoneSearch]);

  const { data: pendingList, status: pendingStatus } = useQuery<EnquiryPopulated[]>(
    pendingUrl,
    fetcher,
    {
      refetchOnWindowFocus: true,
    },
  );

  const { data: doneList, status: doneStatus } = useQuery<EnquiryPopulated[]>(
    doneUrl,
    fetcher,
    {
      enabled: selectedTab === 1,
      refetchOnWindowFocus: true,
    },
  );

  const columns: TableColumnsType<EnquiryPopulated> = useMemo(
    () => [
      {
        width: '14%',
        title: '이름',
        dataIndex: 'name',
        key: 'name',
        align: 'center',
        render: (name: string) => (
          <p className="whitespace-pre-line break-keep text-xs">{name}</p>
        ),
      },
      {
        width: '18%',
        title: '연락처',
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        align: 'center',
        render: (phone: string) => (
          <p className="whitespace-pre-line break-keep text-xs">{phone}</p>
        ),
      },
      {
        width: '14%',
        title: '문의일',
        dataIndex: 'createdAt',
        key: 'createdAt',
        align: 'center',
        render: (date: Date) => (
          <p className="whitespace-pre-line text-xs">{dayjs(date).format('YY.MM.DD')}</p>
        ),
      },
      {
        width: '14%',
        title: '최근답변일',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        align: 'center',
        render: (date: Date, record: EnquiryPopulated) => (
          <p className="whitespace-pre-line text-xs">
            {record.status !== EnquiryStatus.Todo ? dayjs(date).format('YY.MM.DD') : '-'}
          </p>
        ),
      },
      {
        width: '12%',
        title: '상태',
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        render: (status: EnquiryStatus) => {
          const label = STATUS_LABEL[status];
          return <Tag color={label.color}>{label.text}</Tag>;
        },
      },
      {
        width: '10%',
        title: '상세',
        key: 'action',
        align: 'center',
        render: (_: unknown, record: EnquiryPopulated) => (
          <Link to={`${record._id}/reply`}>
            <span className="text-xs font-bold underline theme-text-main">
              {record.status === EnquiryStatus.Done ? '보기' : '답변'}
            </span>
          </Link>
        ),
      },
    ],
    [],
  );

  const currentList = selectedTab === 0 ? pendingList : doneList;
  const currentStatus = selectedTab === 0 ? pendingStatus : doneStatus;

  const onDateRangeChange = useCallback(
    (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
      if (!dates) {
        setDateRange([null, null]);
        return;
      }

      const [start, end] = dates;
      if (start && end && end.diff(start, 'month') > 1) {
        setDateRange([end.subtract(1, 'month'), end]);
        return;
      }
      setDateRange(dates);
    },
    [],
  );

  return (
    <div className="space-y-4">
      <Tabs items={tabItems} selectedTab={selectedTab} setSelectedTab={setSelectedTab} />

      {selectedTab === 1 && (
        <div className="space-y-2 px-4">
          <div className="flex items-center gap-2">
            <RangePicker
              size="small"
              value={dateRange}
              onChange={onDateRangeChange}
              locale={locale}
              className="flex-1"
            />
          </div>
          <Input.Search
            placeholder="전화번호 검색"
            size="small"
            allowClear
            onSearch={(value) => setPhoneSearch(value)}
            enterButton
          />
        </div>
      )}

      <div>
        {currentStatus === 'success' && currentList ? (
          <Table
            className="w-full"
            columns={columns}
            dataSource={currentList}
            pagination={{ position: ['bottomCenter'], pageSize: 15 }}
            rowKey="_id"
            size="small"
            onHeaderRow={() => ({
              className: `text-xs font-bold bg-opacity-40 ${mainBgColorGenerator(
                customTheme,
              )}`,
            })}
          />
        ) : (
          <div className="center-box h-screen-15">
            <Loading />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEnquiryListPage;
